/**
 * FileManager - Manages file operations (open, save, close).
 *
 * Handles DWG file I/O and coordinates related initialization
 * like theme application, ruler visibility, and selection context.
 */

import type { DRDrawingViewer } from "../DRDrawingViewer";
import type { RulerController } from "../overlay";
import type { PhotoMarkerManager } from "./PhotoMarkerManager";
import { selectionService } from "../selection";
import { DrawingSingleton } from "../DrawingSingleton";
import { Logger } from "@/logger";
import { MemoryMonitor } from "@/utils/MemoryMonitor";

/**
 * Thrown when the loaded DrawingWeb.js is the disabled version served by the
 * Protection Server (writeFile1 exists but produces an empty buffer).
 * The SW cache has already been cleared — a page reload will fetch the correct version.
 */
export class DrawingWebDisabledError extends Error {
  constructor() {
    super("DrawingWeb write capability disabled by Protection Server");
    this.name = "DrawingWebDisabledError";
  }
}

export interface FileManagerDeps {
  viewer: DRDrawingViewer;
  rulerController: RulerController | null;
  photoMarkerManager: PhotoMarkerManager | null;
  initializeSelectionContext: () => void;
  applyOverlayTheme: (backgroundColor: number) => void;
  notifyFileOpen: () => void;
}

export class FileManager {
  private deps: FileManagerDeps;

  constructor(deps: FileManagerDeps) {
    this.deps = deps;
  }

  /**
   * Update dependencies (called when they change).
   */
  updateDeps(deps: Partial<FileManagerDeps>): void {
    this.deps = { ...this.deps, ...deps };
  }

  /**
   * Open a DWG file from ArrayBuffer.
   * @throws {DrawingWebDisabledError} if the Protection Server served a disabled
   *   version of DrawingWeb.js. The SW cache is cleared automatically — the caller
   *   should prompt the user to reload the page.
   */
  async open(filename: string, arrayBuffer: ArrayBuffer): Promise<void> {
    await this.deps.viewer.openFile(filename, arrayBuffer);

    // Verify write capability immediately after opening, before the user edits anything.
    const canWrite = await this.deps.viewer.checkWriteCapability();
    if (!canWrite) {
      const e = new DrawingWebDisabledError();
      const cacheInfo = await DrawingSingleton.getDrawingWebCacheInfo();
      Logger.captureException(e, {
        ...cacheInfo,
        bfcacheRestored: Logger.wasBfcacheRestored(),
      });
      await DrawingSingleton.clearDrawingWebCache();
      throw e;
    }

    // Apply overlay theme based on background color
    this.deps.applyOverlayTheme(this.deps.viewer.getEffectiveBackgroundColor());
    // Set selection context with all dependencies
    this.deps.initializeSelectionContext();
    // Clear any previous selection
    selectionService.clear();
    // Initialize photo marker counter from XRecord
    this.deps.photoMarkerManager?.initializeCounter();
    // Notify subscribers that a file is open
    this.deps.notifyFileOpen();
  }

  /**
   * Create a new empty drawing.
   */
  async createNew(): Promise<void> {
    this.deps.viewer.createNew();
    // Apply overlay theme based on background color
    this.deps.applyOverlayTheme(this.deps.viewer.getEffectiveBackgroundColor());
    // Set selection context with all dependencies
    this.deps.initializeSelectionContext();
    // Clear any previous selection
    selectionService.clear();
    // Notify subscribers that a file is open
    this.deps.notifyFileOpen();
  }

  /**
   * Save the current drawing to a DWG Blob.
   * @param filename The filename for the save
   * @returns A Blob containing the DWG file data
   */
  async save(filename: string): Promise<Blob> {
    return this.deps.viewer.saveFile(filename);
  }

  /**
   * Save and trigger a browser download of the drawing.
   * @param filename The filename for the download (defaults to "drawing.dwg")
   */
  async download(filename: string = "drawing.dwg"): Promise<void> {
    const blob = this.deps.viewer.saveFile(filename);
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Check if a file is currently open.
   */
  isOpen(): boolean {
    return this.deps.viewer.isFileOpen();
  }

  /**
   * Close the current file.
   */
  close(): void {
    selectionService.onFileClose();
    this.deps.rulerController?.hide();
    MemoryMonitor.reportOnClose();
    MemoryMonitor.stopSampling();
    this.deps.viewer.closeFile();
  }
}
